
/**
 * WorkflowWindowContent - Embeddable workflow graph viewer for tiled windows
 */

import { useCallback } from 'react';
import { WorkflowGraph } from '@/components/workflow-graph';
import type { WorkflowWindow } from '@/types/tiled-window';
import type { OpenWindowRequest } from '../TiledWindowContext';
import type { AslDefinition, StateNodeData } from '@/types/workflow-graph';
import { buildWorkflowEntityOpenRequest } from '@/components/workflow-graph/utils/qualify-workflow-resource';

interface WorkflowWindowContentProps {
    window: WorkflowWindow;
    /** Callback to open a nested entity window */
    onOpenEntity?: (request: OpenWindowRequest) => void;
}

export function WorkflowWindowContent({ window, onOpenEntity }: WorkflowWindowContentProps) {
    const { data } = window;
    
    if (!data?.workflow) {
        return (
            <div className="flex h-full items-center justify-center text-slate-500">
                No workflow data available
            </div>
        );
    }

    const { workflow, definition, availableWorkflows = [], agents = [], tools = [], models = [], replayRunId } = data;
    const appKey = window.appKey;

    // Handle task node click - open appropriate viewer based on resource type
    const handleTaskClick = useCallback((nodeData: StateNodeData) => {
        if (!onOpenEntity || !nodeData.state?.Resource) return;

        const request = buildWorkflowEntityOpenRequest(
            nodeData.state.Resource,
            nodeData.resourceType ?? 'task',
            {
                appKey,
                resourceAppKey: nodeData.resourceAppKey,
                entityName: nodeData.label,
            },
        );
        if (!request) return;

        onOpenEntity({
            ...request,
            appKey: request.appKey ?? appKey,
        });
    }, [onOpenEntity, appKey]);

    return (
        <div className="h-full w-full">
            <WorkflowGraph
                definition={definition || workflow.definition as unknown as AslDefinition}
                workflowId={workflow.id}
                workflowKey={workflow.key}
                workflowName={workflow.name}
                readOnly={true}
                availableWorkflows={availableWorkflows}
                agents={agents}
                tools={tools}
                models={models}
                hasTests={workflow.has_tests}
                hasRuns={workflow.has_runs}
                onNodeSettingsOpen={handleTaskClick}
                onOpenTiledWindow={onOpenEntity}
                embedded={true}
                appSlug={appKey}
                initialReplayRunId={replayRunId}
            />
        </div>
    );
}

